import { useCallback, useEffect, useMemo } from "react";
import PulseDesktopAutoPlayPagination from "@features/pulse/components/desktop/PulseDesktopAutoPlayPagination";
import PulseDesktopCard from "@features/pulse/components/desktop/PulseDesktopCard";
import PulseDesktopCarouselButton from "@features/pulse/components/desktop/PulseDesktopCarouselButton";
import PulseEndingScreen from "@features/pulse/components/PulseEndingScreen";
import PulsePagination from "@features/pulse/components/PulsePagination";
import { useGetAutoplayTimes } from "@features/pulse/hooks/useGetAutoplayTimes";
import { useGetRandomArbitrary } from "@features/pulse/hooks/useGetRandomArbitrary";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import { CARD_SIZE, CARD_WIDTH, MARGIN } from "@features/pulse/utils/constants";
import { PulsePageProps } from "@features/pulse/utils/types";
import { cn } from "@util/helpers";
import { motion } from "motion/react";

type PulsePagePropsOthers = Omit<PulsePageProps, "handleSetActiveArticle">;

/** Desktop carousel component for pulse */
const PulseDesktopCarousel = ({
  sortedPulseArticles,
  activeArticle,
  handleNavigateRightCard,
  handleNavigateLeftCard,
  offset = 0,
}: PulsePagePropsOthers): React.ReactElement => {
  const { sendCustomEvent } = useSendPulseGaData();
  const { randomNumber } = useGetRandomArbitrary();

  /** Empty for the first card to align the first article in the center */
  const articleList = useMemo(() => {
    return [
      { title: "", summaries: [], slug: "", id: "", url: "", summary: "" },
      ...sortedPulseArticles,
    ];
  }, [sortedPulseArticles]);

  const handleDesktopAutoPlay = useCallback(() => {
    sendCustomEvent(
      `desktop::auto play::${activeArticle}`,
      sortedPulseArticles[activeArticle - 1].title
    );

    handleNavigateRightCard();
  }, [
    activeArticle,
    sortedPulseArticles,
    handleNavigateRightCard,
    sendCustomEvent,
  ]);

  const {
    autoplayTotal,
    isAutoplayEnabled,
    setIsAutoplayEnabled,
    handleSetIsAutoplayPaused,
    resetAutoplayLeft,
  } = useGetAutoplayTimes({
    activeArticle,
    sortedPulseArticles,
    handleNavigateRightCard: handleDesktopAutoPlay,
  });

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const { code } = e;
      const displayTitle =
        sortedPulseArticles?.[activeArticle - 1]?.title || "";

      if (code === "ArrowRight") {
        handleNavigateRightCard();
        resetAutoplayLeft();

        sendCustomEvent(
          `desktop::keyboard press right::${activeArticle}`,
          displayTitle
        );
      }

      if (code === "ArrowLeft") {
        handleNavigateLeftCard();
        resetAutoplayLeft();

        sendCustomEvent(
          `desktop::keyboard press left::${activeArticle}`,
          displayTitle
        );
      }
    },
    [
      handleNavigateRightCard,
      handleNavigateLeftCard,
      resetAutoplayLeft,
      activeArticle,
      sortedPulseArticles,
      sendCustomEvent,
    ]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress, true);

    return function () {
      window.removeEventListener("keydown", handleKeyPress, true);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Disable body scroll when the Pulse carousel is displayed
    window.document.documentElement.style.setProperty(
      "overflow-y",
      "hidden",
      "important"
    );
  }, []);

  if (sortedPulseArticles.length === 0) return <></>;

  const isDisplayEndScreen = activeArticle === articleList.length;

  if (isDisplayEndScreen) {
    return <PulseEndingScreen randomNumber={randomNumber} />;
  }

  return (
    <>
      <div
        className="flex flex-1 flex-col justify-center overflow-hidden"
        data-testid="pulse-desktop-carousel-component"
      >
        <PulsePagination
          activeArticle={activeArticle}
          totalCards={sortedPulseArticles.length}
        />

        <div
          className={cn(
            "relative left-1/2 max-h-[750px] w-full -translate-x-1/2  overflow-hidden"
          )}
          style={{ width: `calc(${CARD_SIZE}px * 3)` }}
        >
          <motion.div
            animate={{
              x: offset,
            }}
            transition={{
              duration: 0.4,
              ease: "linear",
            }}
            className="flex h-full"
          >
            {articleList.map((article, index) => {
              const nextArticle = activeArticle + 1;
              const isDisplayed = [activeArticle, nextArticle].includes(index);

              return (
                <motion.div
                  key={`pulse-individual-card-${article.id}`}
                  className={cn({ "opacity-0": index === 0 })}
                  animate={{
                    opacity: isDisplayed ? 1 : 0,
                    filter: nextArticle === index ? "blur(1.96px)" : "blur(0)",
                    scale: activeArticle < index ? 0.65 : 1,
                    transition: {
                      scale: {
                        duration: 0.35,
                        ease: "linear",
                      },
                    },
                  }}
                  style={{
                    width: `${CARD_WIDTH}px`,
                    margin: `0 ${MARGIN / 2}px`,
                    minWidth: `${CARD_WIDTH}px`,
                  }}
                >
                  <PulseDesktopCard
                    article={article}
                    activeArticle={activeArticle}
                    isActive={activeArticle === index}
                    isPrevious={activeArticle >= index}
                    handleSetIsAutoplayPaused={(isPaused) => {
                      handleSetIsAutoplayPaused(isPaused);
                    }}
                  />
                </motion.div>
              );
            })}
          </motion.div>

          <PulseDesktopCarouselButton
            activeArticle={activeArticle}
            handleNavigateLeftCard={handleNavigateLeftCard}
            handleNavigateRightCard={handleNavigateRightCard}
            resetAutoplayLeft={resetAutoplayLeft}
            displayTitle={sortedPulseArticles[activeArticle - 1].title}
          />
        </div>
      </div>

      <PulseDesktopAutoPlayPagination
        activeArticle={activeArticle}
        sortedPulseArticles={sortedPulseArticles}
        isAutoplayEnabled={isAutoplayEnabled}
        setIsAutoplayEnabled={setIsAutoplayEnabled}
        autoplayTotal={autoplayTotal}
      />
    </>
  );
};

export default PulseDesktopCarousel;
